import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { LogOut, Settings, Loader2, Menu } from "lucide-react";
import { ORG } from "@/lib/constants";

const NAV = [
  { to: "/admin", label: "Overview" },
  { to: "/admin/pledges", label: "Pledges" },
  { to: "/admin/mrs", label: "MR Team" },
  { to: "/admin/links", label: "Pledge Links" },
  { to: "/admin/reports", label: "Reports" },
];

// Shared admin chrome: auth guard, header, section navigation.
export function AdminLayout({ children }) {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currentPwd, setCurrentPwd] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (admin === false) navigate("/admin/login");
  }, [admin, navigate]);

  const saveCredentials = async () => {
    if (!currentPwd) { toast.error("Enter your current password."); return; }
    if (!newEmail.trim() && !newPwd) { toast.error("Enter a new email and/or a new password."); return; }
    if (newPwd && newPwd.length < 8) { toast.error("New password must be at least 8 characters."); return; }
    setSaving(true);
    try {
      await api.post("/auth/change-credentials", {
        current_password: currentPwd,
        new_email: newEmail.trim() || null,
        new_password: newPwd || null,
      });
      toast.success("Credentials updated — please log in again");
      setSettingsOpen(false);
      logout();
      navigate("/admin/login");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  if (!admin || admin === false) return null;

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200">
        <div className="tricolor-bar h-1 w-full" />
        <div className="max-w-[1400px] mx-auto px-5 lg:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2.5 shrink-0">
            <img src={ORG.logo} alt={`${ORG.name} logo`} className="w-9 h-9" />
            <div>
              <p className="font-display font-extrabold text-slate-900 leading-none">Admin</p>
              <p className="text-xs text-slate-500">{ORG.name} · {admin.email}</p>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-1 overflow-x-auto">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={`px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                  pathname === n.to
                    ? "text-[#0056D2] bg-blue-50"
                    : "text-slate-600 hover:text-[#0056D2] hover:bg-slate-100"
                }`}
              >
                {n.label}
              </Link>
            ))}
          </nav>
          <div className="hidden md:flex items-center gap-1 shrink-0">
            <Button data-testid="admin-settings-btn" onClick={() => { setCurrentPwd(""); setNewEmail(""); setNewPwd(""); setSettingsOpen(true); }} variant="ghost" className="rounded-full text-slate-600" title="Change email / password">
              <Settings className="w-4 h-4 mr-2" /> Settings
            </Button>
            <Button data-testid="admin-logout-btn" onClick={() => { logout(); navigate("/admin/login"); }} variant="ghost" className="rounded-full text-slate-600">
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>

          {/* Mobile: navigation drawer */}
          <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
            <SheetTrigger asChild>
              <Button data-testid="admin-drawer-btn" variant="ghost" size="icon" className="md:hidden text-slate-700">
                <Menu className="w-6 h-6" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72" data-testid="admin-drawer">
              <SheetTitle className="font-display font-extrabold text-slate-900 flex items-center gap-2">
                <img src={ORG.logo} alt={`${ORG.name} logo`} className="w-7 h-7" /> Admin
              </SheetTitle>
              <nav className="mt-6 flex flex-col gap-1">
                {NAV.map((n) => (
                  <Link
                    key={n.to}
                    to={n.to}
                    data-testid={`admin-drawer-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => setDrawerOpen(false)}
                    className={`px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                      pathname === n.to
                        ? "text-[#0056D2] bg-blue-50"
                        : "text-slate-700 hover:text-[#0056D2] hover:bg-slate-100"
                    }`}
                  >
                    {n.label}
                  </Link>
                ))}
              </nav>
              <div className="mt-6 border-t border-slate-200 pt-4 flex flex-col gap-1">
                <button
                  type="button"
                  data-testid="admin-drawer-settings"
                  onClick={() => { setDrawerOpen(false); setCurrentPwd(""); setNewEmail(""); setNewPwd(""); setSettingsOpen(true); }}
                  className="flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium text-slate-700 hover:bg-slate-100 text-left"
                >
                  <Settings className="w-4 h-4" /> Settings
                </button>
                <button
                  type="button"
                  data-testid="admin-drawer-logout"
                  onClick={() => { setDrawerOpen(false); logout(); navigate("/admin/login"); }}
                  className="flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium text-red-600 hover:bg-red-50 text-left"
                >
                  <LogOut className="w-4 h-4" /> Logout
                </button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>
      <main className="max-w-[1400px] mx-auto px-5 lg:px-8 py-8 space-y-8">{children}</main>

      {/* Admin credentials dialog */}
      <Dialog open={settingsOpen} onOpenChange={setSettingsOpen}>
        <DialogContent className="max-w-sm rounded-2xl">
          <DialogTitle className="font-display font-bold text-xl text-slate-900">Admin Settings</DialogTitle>
          <div className="space-y-4">
            <div>
              <Label htmlFor="cur-pwd" className="text-sm font-medium text-slate-700">Current password</Label>
              <Input data-testid="admin-current-pwd" id="cur-pwd" type="password" value={currentPwd} onChange={(e) => setCurrentPwd(e.target.value)} className="mt-1.5" autoComplete="current-password" />
            </div>
            <div>
              <Label htmlFor="new-email" className="text-sm font-medium text-slate-700">New email <span className="text-slate-400 font-normal">(leave blank to keep {admin.email})</span></Label>
              <Input data-testid="admin-new-email" id="new-email" type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} className="mt-1.5" placeholder={admin.email} autoComplete="email" />
            </div>
            <div>
              <Label htmlFor="new-pwd" className="text-sm font-medium text-slate-700">New password <span className="text-slate-400 font-normal">(min 8 chars, blank to keep)</span></Label>
              <Input data-testid="admin-new-pwd" id="new-pwd" type="password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} className="mt-1.5" autoComplete="new-password" />
            </div>
            <Button data-testid="admin-save-creds" onClick={saveCredentials} disabled={saving} className="w-full rounded-full bg-[#0056D2] hover:bg-blue-700 text-white font-semibold">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Update credentials"}
            </Button>
            <p className="text-xs text-slate-400 text-center">You will be logged out and asked to sign in with the new credentials.</p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
